import React from 'react';
import styled, { css } from 'styled-components';
import { tokens } from '@carta/ink';
import { CodeWithAllStyles } from './CodeStyles';

interface StyledTextProps {
  variant?:
    | 'body-1'
    | 'body-2'
    | 'small-1'
    | 'heading-2'
    | 'guidelines-heading-2'
    | 'guidelines-heading-3'
    | 'guidelines-heading-4'
    | 'guidelines-code';
  weight?: 'medium' | 'bold';
  color?: 'red' | 'green' | 'gray' | 'white' | 'default' | 'description';
}

export const defaultText = css`
  font-family: 'Plus Jakarta Sans' !important;
  color: ${tokens.color.global.font.hierarchy.body.default};
  font-style: normal;
  margin: 0;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;
  text-align: left;
`;

const StyledTextBase = styled.p<StyledTextProps>`
  font-family: 'Plus Jakarta Sans' !important;
  font-style: normal;
  margin: 0;

  ${props => {
    switch (props.variant) {
      case 'body-1':
        return `
          font-size: 16px;
          line-height: 30px;
          font-weight: 600;
        `;
      case 'body-2':
        return `
          font-size: 16px;
          line-height: 28px;
          font-weight: 400;
        `;
      case 'small-1':
        return `
          font-size: 12px;
          line-height: 14px;
          font-weight: 400;
          color: #656B6B
        `;
      case 'heading-2':
        return `
          font-size: 20px;
          line-height: normal;
          font-weight: 600;
        `;
      case 'guidelines-heading-2':
        return `
          font-size: 20px;
          line-height: 150%;
          font-weight: 600;
          letter-spacing: 0%;
        `;
      case 'guidelines-heading-3':
        return `
          font-size: 14px;
          line-height: 150%;
          font-weight: 600;
          letter-spacing: 0%;
        `;
      case 'guidelines-heading-4':
        return `
          font-size: 14px;
          line-height: 150%;
          font-weight: 600;
          letter-spacing: 0%;
        `;
      default:
        return defaultText;
    }
  }}

  ${props => {
    switch (props.weight) {
      case 'medium':
        return 'font-weight: 500;';
      case 'bold':
        return 'font-weight: 600;';
      default:
        return '';
    }
  }}

  ${props => {
    switch (props.color) {
      case 'red':
        return `color: ${tokens.color.global.feedback.negative['intensity-70']};`;
      case 'green':
        return `color: ${tokens.color.global.feedback.positive['intensity-70']};`;
      case 'gray':
        return `color: ${tokens.color.global.font.progression.complete.default};`;
      case 'white':
        return `color: ${tokens.color.global.font.custom.white.default};`;
      case 'description':
        return `color: #656b6b;`; // gray-80
      default:
        return '';
    }
  }}
`;

// Wrapper component that renders CodeWithAllStyles for guidelines-code variant, otherwise StyledTextBase
const StyledText: React.FC<StyledTextProps & React.HTMLAttributes<HTMLElement>> = ({ variant, children, ...props }) => {
  if (variant === 'guidelines-code') {
    return <CodeWithAllStyles {...props}>{children}</CodeWithAllStyles>;
  }
  return (
    <StyledTextBase variant={variant} {...props}>
      {children}
    </StyledTextBase>
  );
};

export default StyledText;
